import { map } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { Component, AfterViewInit, Input, ViewChild, ElementRef, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { AddressAutofillCore, AddressAutofillSuggestionResponse, SessionToken } from '@mapbox/search-js-core';
import { environment } from 'src/environments/environment';
import { IdProvider } from '../id-provider';

interface Address {
  id: string;
  name: string;
  address?: string;
  city?: string;
  zip?: string;
  country?: string;
}

@Component({
  selector: 'app-address-autofill',
  imports: [TranslateModule, AutocompleteComponent],
  templateUrl: './address-autofill.component.html',
  styleUrl: './address-autofill.component.scss'
})
export class AddressAutofillComponent implements AfterViewInit, OnInit {

  @Input() public addressForm!: FormGroup;

  @Input()
  public id: string = `app-address-autofill-${IdProvider.getNextId()}`;

  @ViewChild('fullAddressInput', { static: false }) fullAddressInputRef!: ElementRef<HTMLInputElement>;

  public get selectedAddress(): string {
    return this.addressForm.controls.addressCompletionId.value ?? this.addressForm.controls.fullAddress.value;
  }

  public set selectedAddress(value: any) {
    const suggestion = this._cachedAddressSuggestions.find(s => s.id === value);
    this.addressForm.controls.fullAddress.markAsDirty();
    if (suggestion) {
      this.addressForm.controls.addressCompletionId.setValue(suggestion.id);
      this.addressForm.controls.addressAutocompleteSessionToken.setValue(this._autofillSessionToken.id);
      this.addressForm.controls.fullAddress.setValue(suggestion.name);
      this.setOptionalControls({
        address: suggestion.address,
        city: suggestion.city,
        zip: suggestion.zip,
        country: suggestion.country
      });
    } else {
      this.addressForm.controls.addressCompletionId.setValue(null);
      this.addressForm.controls.addressAutocompleteSessionToken.setValue(null);
      this.addressForm.controls.fullAddress.setValue(null);
      this.setOptionalControls({
        address: null,
        city: null,
        zip: null,
        country: null
      });
    }
  }

  private _autofillCore: AddressAutofillCore;
  private _autofillSessionToken: SessionToken;

  private _cachedAddressSuggestions: Address[] = [];

  private readonly _initialId = 'orig';

  constructor() {}

  private checkRequiredAddressFields() {
    const requiredFields: string[] = ['fullAddress', 'addressCompletionId', 'addressAutocompleteSessionToken' ];
    const missing = requiredFields.filter(f => !this.addressForm?.get(f));
    if (missing.length > 0) {
      throw new Error(`AddressAutofillComponent: Missing required form controls: ${missing.join(', ')}`);
    }
  }

  ngOnInit(): void {
    this._autofillSessionToken = new SessionToken();
    this._autofillCore = new AddressAutofillCore({accessToken: environment.mapbox.accessToken});
    this._cachedAddressSuggestions = [{id: this._initialId, name: this.addressForm.controls.fullAddress.value, address: null, city: null, zip: null, country: null}];
  }

  ngAfterViewInit(): void {
    this.checkRequiredAddressFields();
  }

  public getAddress(query: string): Observable<any[]> {
    this.addressForm.controls.addressCompletionId.setValue(null);
    this.addressForm.controls.addressAutocompleteSessionToken.setValue(null);
    if (!query) {
      return of(this._cachedAddressSuggestions);
    }
    if (query.length < 3 || !this._autofillCore || query === this.addressForm.controls.fullAddress.value) {
      this._cachedAddressSuggestions = [];
      return of(this._cachedAddressSuggestions);
    }

    return from(this._autofillCore.suggest(query, {sessionToken: this._autofillSessionToken, proximity: 'ip', streets: false})).pipe(
      map((response: AddressAutofillSuggestionResponse) => {
        this._cachedAddressSuggestions = response.suggestions.map(suggestion => ({
          id: suggestion.mapbox_id,
          name: suggestion.full_address,
          address: suggestion.address_line1,
          city: suggestion.address_level2,
          zip: suggestion.postcode,
          country: suggestion.country
        }));
        return this._cachedAddressSuggestions;
      })
    );
  }

  private setOptionalControls(values: Partial<Record<string, any>>): void {
    Object.entries(values).forEach(([key, value]) => {
      if (this.addressForm.controls[key]) {
        this.addressForm.controls[key].setValue(value);
      }
    });
  }
}
